import React, { useEffect, useMemo, useState } from 'react'
import { t } from 'ttag'
import { Box, IconButton, ListItem, ListItemIcon, ListItemText, Typography } from '@material-ui/core'
import { type MRT_ColumnDef } from 'material-react-table'
import YumButton from '../../components/YumButton/YumButton'
import YumInput from '../../components/YumInput/YumInput'
import YumTable from '../../components/YumTable/YumTable'
import YumDialog from '../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../components/YumDialogContent/YumDialogContent'
import YumDialogActions from '../../components/YumDialogActions/YumDialogActions'
import YumDialogContentText from '../../components/YumDialogContentText/YumDialogContentText'
import YumCancelButton from '../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../components/YumSubmitButton/YumSubmitButton'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import YumHeader from '../../components/YumHeader/YumHeader'
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage'
import { CompanyDomain, UserCompanyDto } from '../../services/api.types'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import YumCopyButton from '../../components/YumCopyButton/YumCopyButton'
import YumTooltip from '../../components/YumTooltip/YumTooltip'
import { Button } from '../../components/Button/Button'
import YumRemoveButton from '../../components/YumRemoveButton/YumRemoveButton'
import YumSwitch from '../../components/YumSwitch/YumSwitch'
import { ReactComponent as EmailIcon } from '../../assets/v3/icons/announcements/email.svg'
import { ReactComponent as GoogleIcon } from '../../assets/v2/icons/icon-google-mono.svg'
import DomainStatus from './components/DomainStatus/DomainStatus'

// --- Props ---
export interface IDomainAdminProps {
  managedCompanies: UserCompanyDto[]
  domains: CompanyDomain[]
  loading: boolean
  adding: boolean
  removing: boolean
  verifying: boolean
  selectedCompanyId: string | null
  addedDomain: CompanyDomain | null
  emailSignupDomain: boolean
  googleSignupDomain: boolean
  settingsLoading: boolean
  settingsUpdating: boolean
}

export interface IDomainAdminDispatchProps {
  getCompanyDomains: (payload: { companyId: string }) => void
  addCompanyDomain: (payload: { companyId: string; domain: string }) => void
  removeCompanyDomain: (payload: { companyId: string; domainId: string }) => void
  verifyCompanyDomain: (payload: { companyId: string; domainId: string }) => void
  setDomainSelectedCompanyId: (payload: string) => void
  getCompanyDomainSettings: (payload: { companyId: string }) => void
  updateCompanyDomainSettings: (payload: {
    companyId: string
    emailSignupDomain: boolean
    googleSignupDomain: boolean
  }) => void
  clearAddedDomain: () => void
  clearNotification: () => void
}

type DomainAdminProps = IDomainAdminProps & IDomainAdminDispatchProps

/**
 * Admin page for managing company email domains.
 *
 * Allows admins to add, verify, and remove email domains.
 * Verified domains are required for SSO assertions to be trusted,
 * preventing email forwarding attacks.
 */
export default function DomainAdmin(props: DomainAdminProps) {
  const {
    managedCompanies,
    domains,
    loading,
    adding,
    removing,
    verifying,
    selectedCompanyId,
    addedDomain,
    emailSignupDomain,
    googleSignupDomain,
    settingsLoading,
    settingsUpdating,
    getCompanyDomains,
    addCompanyDomain,
    removeCompanyDomain,
    verifyCompanyDomain,
    setDomainSelectedCompanyId,
    getCompanyDomainSettings,
    updateCompanyDomainSettings,
    clearAddedDomain,
    clearNotification,
  } = props

  // Add domain dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [domainError, setDomainError] = useState('')
  // Remove confirmation dialog state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [removeDomainId, setRemoveDomainId] = useState<string | null>(null)
  // DNS info dialog state
  const [infoDomain, setInfoDomain] = useState<CompanyDomain | null>(null)
  // Track which domain is being removed/verified (for per-row loading)
  const [actionDomainId, setActionDomainId] = useState<string | null>(null)
  // Settings dialog state
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [localEmailSignup, setLocalEmailSignup] = useState(false)
  const [localGoogleSignup, setLocalGoogleSignup] = useState(false)

  // Select first managed company by default
  const companyId = selectedCompanyId || (managedCompanies.length > 0 ? managedCompanies[0].companyId : null)

  // Set default company on first mount
  useEffect(() => {
    if (companyId && !selectedCompanyId) {
      setDomainSelectedCompanyId(companyId)
    }
  }, [])

  // Load domains and settings whenever selected company changes
  useEffect(() => {
    if (companyId) {
      getCompanyDomains({ companyId })
      getCompanyDomainSettings({ companyId })
    }
  }, [companyId])

  // Clear per-row loading state when removing/verifying finishes
  useEffect(() => {
    if (!removing && !verifying) {
      setActionDomainId(null)
    }
  }, [removing, verifying])

  // Handle company selection change
  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newCompanyId = event.target.value as string
    setDomainSelectedCompanyId(newCompanyId)
    getCompanyDomains({ companyId: newCompanyId })
    getCompanyDomainSettings({ companyId: newCompanyId })
  }

  // Validate domain format (e.g. "example.com", "sub.example.co.uk")
  const isValidDomain = (domain: string): boolean => {
    const pattern = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/
    return pattern.test(domain)
  }

  // Open the add domain dialog
  const handleDialogOpen = () => {
    clearNotification()
    clearAddedDomain()
    setDialogOpen(true)
    setNewDomain('')
    setDomainError('')
  }

  // Close the add domain dialog
  const handleDialogClose = () => {
    if (!adding) {
      setDialogOpen(false)
    }
  }

  // Handle domain input change
  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewDomain(e.target.value)
    // Clear error when user starts typing
    if (domainError) setDomainError('')
  }

  // Handle adding a new domain
  const handleAddDomain = () => {
    const trimmed = newDomain.trim().toLowerCase()
    if (!isValidDomain(trimmed)) {
      setDomainError(t`Please enter a valid domain (e.g. example.com)`)
      return
    }
    if (!companyId || !trimmed || adding) return

    addCompanyDomain({ companyId, domain: trimmed })
  }

  // Handle domain input keypress (submit on Enter)
  const handleDomainKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newDomain.trim() && !adding) {
      handleAddDomain()
    }
  }

  // Open remove confirmation dialog
  const handleRemove = (domainId: string) => {
    setRemoveDomainId(domainId)
    setRemoveDialogOpen(true)
  }

  // Confirm and execute domain removal
  const handleConfirmRemove = () => {
    if (!companyId || !removeDomainId) return
    setActionDomainId(removeDomainId)
    removeCompanyDomain({ companyId, domainId: removeDomainId })
    setRemoveDialogOpen(false)
    setRemoveDomainId(null)
  }

  // Close remove confirmation dialog
  const handleRemoveDialogClose = () => {
    if (!removing) {
      setRemoveDialogOpen(false)
      setRemoveDomainId(null)
    }
  }

  // Handle verifying a domain
  const handleVerify = (domainId: string) => {
    if (!companyId) return
    setActionDomainId(domainId)
    verifyCompanyDomain({ companyId, domainId })
  }

  // Settings dialog handlers
  const handleSettingsOpen = () => {
    setLocalEmailSignup(emailSignupDomain)
    setLocalGoogleSignup(googleSignupDomain)
    setSettingsOpen(true)
  }

  const handleSettingsClose = () => {
    if (!settingsUpdating) setSettingsOpen(false)
  }

  const handleSettingsSave = () => {
    if (!companyId || settingsUpdating) return
    updateCompanyDomainSettings({
      companyId,
      emailSignupDomain: localEmailSignup,
      googleSignupDomain: localGoogleSignup,
    })
  }

  // Close settings dialog when update completes successfully
  const prevSettingsUpdating = React.useRef(false)
  useEffect(() => {
    if (prevSettingsUpdating.current && !settingsUpdating && settingsOpen) {
      setSettingsOpen(false)
    }
    prevSettingsUpdating.current = settingsUpdating
  }, [settingsUpdating])

  // Render DNS TXT record instructions for a domain
  const renderDnsInfo = (domain: CompanyDomain) => (
    <Box>
      <Typography variant="body2" style={{ marginBottom: 12 }}>
        {t`To verify ownership of`} <strong style={{ fontFamily: 'monospace' }}>{domain.domain}</strong>
        {', '}
        {t`add the following TXT record to your domain's DNS settings:`}
      </Typography>
      <Box
        style={{
          padding: '12px 16px',
          borderRadius: 8,
          backgroundColor: '#f5f5f5',
          marginBottom: 12,
        }}
      >
        <Typography variant="body2" style={{ color: '#666', marginBottom: 4 }}>
          {t`Record type`}
        </Typography>
        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
          TXT
        </Typography>
        <Typography variant="body2" style={{ color: '#666', marginBottom: 4 }}>
          {t`Host / Name`}
        </Typography>
        <Typography variant="body2" style={{ fontWeight: 600, fontFamily: 'monospace', marginBottom: 8 }}>
          @ ({domain.domain})
        </Typography>
        <Typography variant="body2" style={{ color: '#666', marginBottom: 4 }}>
          {t`Value`}
        </Typography>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Typography
            variant="body2"
            style={{
              fontWeight: 600,
              fontFamily: 'monospace',
              wordBreak: 'break-all',
            }}
          >
            {domain.verificationToken}
          </Typography>
          <YumCopyButton text={domain.verificationToken} />
        </Box>
      </Box>
      <Typography variant="body2" style={{ color: '#666', fontSize: '0.8125rem' }}>
        {t`DNS changes may take up to 48 hours to propagate. You can verify the domain later from the domain list.`}
      </Typography>
    </Box>
  )

  // Format timestamp to readable date
  const formatDate = (timestamp: string) => {
    try {
      const date = new Date(timestamp)
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch {
      return timestamp
    }
  }

  // Table column definitions
  const columns = useMemo<MRT_ColumnDef<CompanyDomain>[]>(
    () => [
      {
        accessorKey: 'domain',
        header: t`Domain`,
        Cell: ({ row: { original } }: any) => (
          <Typography variant="body2" style={{ fontWeight: 600, fontFamily: 'monospace' }}>
            {original.domain}
          </Typography>
        ),
      },
      {
        accessorKey: 'status',
        header: t`Status`,
        size: 120,
        Cell: ({ row: { original } }: any) => <DomainStatus status={original.status} />,
      },
      {
        accessorKey: 'createdAt',
        header: t`Added at`,
        size: 140,
        Cell: ({ row: { original } }: any) => <Typography variant="body2">{formatDate(original.createdAt)}</Typography>,
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        enableSorting: false,
        muiTableHeadCellProps: { align: 'right' },
        Cell: ({ row: { original } }: any) => {
          const isActionTarget = actionDomainId === original.id
          return (
            <Box style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', width: '100%' }}>
              {original.status?.toLowerCase() !== 'verified' && (
                <>
                  <Button link onClick={() => handleVerify(original.id)} disabled={isActionTarget && verifying}>
                    {t`Verify`}
                  </Button>
                  <YumTooltip title={t`DNS info`} arrow placement="top">
                    <IconButton
                      onClick={() => setInfoDomain(original)}
                      style={{ borderRadius: '10px' }}
                      aria-label={t`DNS info`}
                    >
                      <InfoOutlined style={{ width: 24, height: 24 }} aria-hidden="true" />
                    </IconButton>
                  </YumTooltip>
                </>
              )}
              <YumRemoveButton onClick={() => handleRemove(original.id)} />
            </Box>
          )
        },
      },
    ],
    [actionDomainId, verifying, removing],
  )

  return (
    <Card>
      <NotificationsWrapper />

      <YumHeader>
        <h1>
          {t`Domains`}
          {!loading && <span style={{ opacity: 0.5, whiteSpace: 'nowrap' }}> ({domains.length})</span>}
        </h1>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <YumButton variant="contained" color="primary" size="small" onClick={handleSettingsOpen}>
            {t`Settings`}
          </YumButton>
          {managedCompanies.length > 1 && (
            <YumSmallSelect
              variant="outlined"
              value={companyId}
              onChange={handleCompanyChange}
              inputProps={{ 'aria-label': t`Company`, 'data-a11y-label': t`Company` }}
              className="show-on-desktop"
            >
              {managedCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
                <option key={keyIndex} value={userCompany.companyId}>
                  {userCompany.companyName}
                </option>
              ))}
            </YumSmallSelect>
          )}
        </Box>
      </YumHeader>
      {managedCompanies.length > 1 && (
        <YumSmallSelect
          variant="outlined"
          value={companyId}
          onChange={handleCompanyChange}
          inputProps={{ 'aria-label': t`Company`, 'data-a11y-label': t`Company` }}
          className="hide-on-desktop"
          fullWidth
          //style={{ marginBottom: 16 }}
        >
          {managedCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
            <option key={keyIndex} value={userCompany.companyId}>
              {userCompany.companyName}
            </option>
          ))}
        </YumSmallSelect>
      )}

      {/*<Typography variant="body2" style={{ color: '#666', marginBottom: 16 }}>
        {t`Having a verified domain is mandatory for SSO configuration. Verified domains can also be used to limit access on email and password sign-ups.`}
      </Typography>*/}

      {/* Add domain dialog */}
      <YumDialog open={dialogOpen}>
        <YumDialogTitle handleClose={handleDialogClose}>
          {addedDomain ? t`DNS verification` : t`Add domain`}
        </YumDialogTitle>
        <YumDialogContent>
          <NotificationsWrapper />
          {!addedDomain ? (
            <>
              <YumInput
                autoFocus
                label={t`Domain`}
                value={newDomain}
                onChange={handleDomainChange}
                onKeyDown={handleDomainKeyDown}
                placeholder="example.com"
                style={{ marginBottom: 0 }}
                disabled={adding}
              />
              {domainError && <ErrorMessage>{domainError}</ErrorMessage>}
            </>
          ) : (
            renderDnsInfo(addedDomain)
          )}
        </YumDialogContent>
        <YumDialogActions>
          {!addedDomain ? (
            <>
              <YumCancelButton onClick={handleDialogClose} disabled={adding} />
              <YumSubmitButton
                text={t`Add`}
                onClick={handleAddDomain}
                disabled={adding}
                submitInProgress={adding}
                immersiveLoader={true}
              />
            </>
          ) : (
            <YumSubmitButton text={t`Done`} onClick={handleDialogClose} />
          )}
        </YumDialogActions>
      </YumDialog>

      {/* Remove domain confirmation dialog */}
      <YumDialog open={removeDialogOpen}>
        <YumDialogTitle handleClose={handleRemoveDialogClose}>{t`Remove domain`}</YumDialogTitle>
        <YumDialogContent>
          <YumDialogContentText>
            {t`Are you sure you want to remove this domain? This may affect SSO configuration and sign-up restrictions.`}
          </YumDialogContentText>
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={handleRemoveDialogClose} />
          <YumSubmitButton text={t`Remove`} onClick={handleConfirmRemove} color="error" />
        </YumDialogActions>
      </YumDialog>

      {/* DNS info dialog */}
      <YumDialog open={!!infoDomain}>
        <YumDialogTitle handleClose={() => setInfoDomain(null)}>{t`DNS verification`}</YumDialogTitle>
        <YumDialogContent>{infoDomain && renderDnsInfo(infoDomain)}</YumDialogContent>
        <YumDialogActions>
          <YumSubmitButton text={t`Done`} onClick={() => setInfoDomain(null)} />
        </YumDialogActions>
      </YumDialog>

      {/* Domain settings dialog */}
      <YumDialog open={settingsOpen}>
        <YumDialogTitle handleClose={handleSettingsClose}>{t`Domain settings`}</YumDialogTitle>
        <YumDialogContent>
          <ListItem
            role={undefined}
            dense
            button
            onClick={() => setLocalEmailSignup(!localEmailSignup)}
            style={{ marginBottom: 10 }}
          >
            <ListItemIcon>
              <EmailIcon width={24} height={24} />
            </ListItemIcon>
            <ListItemText
              primary={t`Restrict sign-up with email to verified domains only`}
              /*primary={t`Restrict email sign-up`}
              secondary={t`Restrict sign-up with email to verified domains only`}*/
            />
            <ListItemIcon>
              <YumSwitch checked={localEmailSignup} disabled={settingsUpdating} />
            </ListItemIcon>
          </ListItem>
          <ListItem
            role={undefined}
            dense
            button
            onClick={() => setLocalGoogleSignup(!localGoogleSignup)}
            style={{ marginBottom: 10 }}
          >
            <ListItemIcon>
              <GoogleIcon />
            </ListItemIcon>
            <ListItemText primary={t`Allow Google Sign-In for verified domains`} />
            <ListItemIcon>
              <YumSwitch checked={localGoogleSignup} disabled={settingsUpdating} />
            </ListItemIcon>
          </ListItem>
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={handleSettingsClose} submitInProgress={settingsUpdating} />
          <YumSubmitButton
            text={t`Save`}
            onClick={handleSettingsSave}
            submitInProgress={settingsUpdating}
            immersiveLoader={true}
          />
        </YumDialogActions>
      </YumDialog>

      <div style={{ marginTop: '10px' }}>
        <YumTable
          enableRowNumbers={false}
          columns={columns as any}
          data={domains}
          getRowId={(row: any) => row.id as string}
          state={{ isLoading: loading }}
          initialState={{
            sorting: [{ id: 'domain', desc: false }],
            density: 'compact',
            columnPinning: { right: ['actions'] },
          }}
          renderTopToolbarCustomActions={() => (
            <YumButton color="secondary" variant="contained" size="small" onClick={handleDialogOpen}>
              {t`Add domain`}
            </YumButton>
          )}
        />
      </div>
    </Card>
  )
}
